import { DropzoneFile } from 'components/Dropzone/utils';
import { User } from './User';
import { DataRoom } from 'router/subrouters/DataRoom/models/DataRoom';
import { DataRoomNda } from 'router/subrouters/DataRoom/models/DataRoomNDA';
import { ObjectValues } from 'types';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';

export type ShareDataRoomNodes = { fileIds: string[]; folderIds: string[] };

export type ShareableLinkSecurityInfo = {
  hasPassword: boolean;

  hasNDA: boolean;

  hasTermsAndConditions?: boolean; // false if already accepted
  termsAndConditionsContent?: string;
};

export type ShareableLinkMainInfo = ShareableLinkSecurityInfo & {
  isSellSideMemberAccess?: boolean;

  isLead?: boolean;
  isWithdrawnLead?: boolean;

  dataRoomId: string;

  dealRoomId?: string;
  dealRoomName?: string;

  doesNotExist: boolean;
  hasExpired: boolean;

  doesNotHaveAccess: boolean;
  requestedAccessAt?: Date;

  requestedPasswordAt?: Date;

  ndaStatus?: ShareableLinkMainInfoNDAStatus;

  addedToWaitingRoom: boolean;
  rejectedFromWaitingRoom: boolean;
};

type BaseDataRoomShareableLink = {
  name: string;

  linkAccess: DataRoomShareableLinkAccess;

  // for add_password
  password?: string;
  recipientEmailAddresses?: string[];

  linkDestination?: DataRoomShareableLinkDestination;
  optionalNote?: string;
  anyoneWithNonPublicDomain?: boolean;
  emailDomain?: string;

  // security
  linkExpires: DataRoomShareableLinkExpires;
  linkExpiresDate?: Date;
  allowDownloads: boolean;

  // terms and conditions
  includeTermsAndConditionProtection: boolean;
  termsAndConditionsContent?: string;

  // NDA
  includeNdaProtection: boolean;
  dataRoomNdaId?: string;
  ndaApplyTo?: DataRoomNdaApplyTo;

  notifyOnSecureLinkAccess: boolean;
  notifyOnWaitingRoomEntry: boolean;
};

export type DataRoomShareableLink = BaseDataRoomShareableLink &
  ShareDataRoomNodes & {
    _id: string;

    // for specific_recipients
    // initial emailAddressesWithAccess === recipientEmailAddresses; but can be extended
    emailAddressesWithAccess: string[];

    isActive: boolean;

    //sort props
    sortName: string;
    accessNumber: number;
    lastAccessDate: Date;

    totalAccessCount: number;

    createdAt: Date;
    updatedAt: Date;

    userId: string;
    user: Partial<User> | null;

    dataRoomId: string;
    dataRoom: Partial<DataRoom> | null;

    // NDA
    dataRoomNda?: DataRoomNda;
  };

export const DATA_ROOM_NDA_APPLY_TO = {
  ONLY_UNSIGNED: 'only_unsigned',
  EVERYONE: 'everyone',
} as const;

export type DataRoomNdaApplyTo = ObjectValues<typeof DATA_ROOM_NDA_APPLY_TO>;

export const DATA_ROOM_SHAREABLE_LINK_ACCESS = {
  ANYONE_WITH_LINK: 'anyone_with_link',
  ADD_PASSWORD: 'add_password',
  SPECIFIC_RECIPIENTS: 'specific_recipients',
} as const;

export type DataRoomShareableLinkAccess = ObjectValues<
  typeof DATA_ROOM_SHAREABLE_LINK_ACCESS
>;

export const DATA_ROOM_SHAREABLE_LINK_EXPIRES = {
  SEVEN_DAYS: 'seven_days',
  THIRTY_DAYS: 'thirty_days',
  OTHER: 'other',
} as const;

export type DataRoomShareableLinkExpires = ObjectValues<
  typeof DATA_ROOM_SHAREABLE_LINK_EXPIRES
>;

export const DATA_ROOM_SHAREABLE_LINK_DESTINATION = {
  WAITING_ROOM: 'waiting_room',
  DIRECT_ACCESS: 'direct_access',
} as const;

export type DataRoomShareableLinkDestination = ObjectValues<
  typeof DATA_ROOM_SHAREABLE_LINK_DESTINATION
>;

export type CreateDataRoomShareableLinkForm = BaseDataRoomShareableLink & {
  // NDA
  useExistingNda?: boolean;
  dataRoomNda?: DropzoneFile;

  recipientEmailAddressesUnsubmited?: string;
};

export type CreateDataRoomShareableLinkPayload =
  CreateDataRoomShareableLinkForm & ShareDataRoomNodes;

const DATA_ROOM_SHAREABLE_LINK_REQUEST_TYPE = {
  REQUEST_ACCESS: 'request_access',
  REQEUST_PASSWORD: 'request_password',
} as const;

type DataRoomShareableLinkRequestType = ObjectValues<
  typeof DATA_ROOM_SHAREABLE_LINK_REQUEST_TYPE
>;

const DATA_ROOM_SHAREABLE_LINK_REQUEST_STATUS = {
  REQUESTED: 'requested',
  GRANTED: 'granted',
  REJECTED: 'rejected',
} as const;

type DataRoomShareableLinkRequestStatus = ObjectValues<
  typeof DATA_ROOM_SHAREABLE_LINK_REQUEST_STATUS
>;

export type DataRoomShareableLinkRequest = {
  _id: string;

  type: DataRoomShareableLinkRequestType;
  status: DataRoomShareableLinkRequestStatus;
  requestedAt: Date;

  dataRoomShareableLinkId: string;
  dataRoomShareableLink: DataRoomShareableLink;

  dataRoomShareableLinkConsumerId: string;
  dataRoomShareableLinkConsumer: User;

  createdAt: Date;
  updatedAt: Date;
};

export const SHAREABLE_LINK_MAIN_INFO_NDA_STATUS = {
  READY_FOR_SIGNING: 'ready_for_signing',
  WAITING_FOR_CONNECTION: 'waiting_for_connection',
  WAITING_FOR_COUNTER_SIGN: 'waiting_for_counter_sign',
  REJECTED: 'rejected',
} as const;

export type ShareableLinkMainInfoNDAStatus = ObjectValues<
  typeof SHAREABLE_LINK_MAIN_INFO_NDA_STATUS
>;

export type ShareableLinkAnalyticsFileEntry = {
  dataRoomNode: DataRoomNode;
  numberOfViews: number;
  numberOfDownloads: number;
  numberOfSecondsSpent: number;
  numberOfViewedPages: number;
  numberOfPages: number;
};

export type ShareableLinkAnalyticsMember = {
  _id: string;
  user: User | null;
  visitCount: number;
  lastAccessDate: Date | null;
};

export type ShareableLinkAnalyticsMemberEntry = {
  member: ShareableLinkAnalyticsMember;
  files: ShareableLinkAnalyticsFileEntry[];
};

export type ShareableLinkAnalyticsResponse = {
  members: ShareableLinkAnalyticsMemberEntry[];
  totalAccessCount: number;
  uniqueVisitorsCount: number;
  lastAccessDate: Date | null;
};
